import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, TrendingUp, Calendar, Wallet, Calculator } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import merchantImg from "@/assets/merchant.webp";
import { Riyal } from "@/components/ui/riyal";

const SERVICE_FEE = 0.15;

function EarningsCalculator() {
  const { t, i18n } = useTranslation();
  const [rate, setRate] = useState(45);
  const [hours, setHours] = useState(6);
  const [days, setDays] = useState(18);

  const { gross, fee, net } = useMemo(() => {
    const gross = rate * hours * days;
    const fee = gross * SERVICE_FEE;
    return { gross, fee, net: gross - fee };
  }, [rate, hours, days]);

  const locale = i18n.language === "ar" ? "ar-SA" : "en-US";
  const fmt = (n: number) =>
    n.toLocaleString(locale, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  return (
    <div className="space-y-6">
      <Field
        label={t("forHostsSection.hourlyRate")}
        value={`${fmt(rate)} ${t("forHostsSection.perHr")}`}
        sliderValue={rate}
        min={10}
        max={250}
        step={5}
        onChange={setRate}
      />
      <Field
        label={t("forHostsSection.hoursPerDay")}
        value={`${hours} ${t("forHostsSection.hrs")}`}
        sliderValue={hours}
        min={1}
        max={12}
        step={1}
        onChange={setHours}
      />
      <Field
        label={t("forHostsSection.daysPerMonth")}
        value={`${days} ${t("forHostsSection.days")}`}
        sliderValue={days}
        min={1}
        max={30}
        step={1}
        onChange={setDays}
      />

      <div className="rounded-2xl border border-border bg-surface p-5">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t("forHostsSection.estMonthly")}
        </div>
        <div className="mt-2 font-display text-4xl font-bold text-foreground">{fmt(net)}</div>
        <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
          <Row label={t("forHostsSection.gross")} value={fmt(gross)} />
          <Row label={`${t("forHostsSection.serviceFee")} (${Math.round(SERVICE_FEE * 100)}%)`} value={`− ${fmt(fee)}`} />
        </div>
      </div>

      <Link
        to="/partner"
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-foreground px-5 py-3 text-sm font-semibold text-background transition-opacity hover:opacity-90"
      >
        {t("forHostsSection.startListingSpace")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
      </Link>
    </div>
  );
}

function Field({
  label,
  value,
  sliderValue,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: string;
  sliderValue: number;
  min: number;
  max: number;
  step: number;
  onChange: (n: number) => void;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <Label className="text-sm font-medium text-foreground">{label}</Label>
        <span className="text-sm font-semibold text-foreground">{value}</span>
      </div>
      <Slider
        className="mt-3"
        value={[sliderValue]}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(v[0])}
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span>{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}

export function ForMerchants() {
  const { t } = useTranslation();
  const bullets = [
    { icon: TrendingUp, text: t("forHostsSection.b1") },
    { icon: Calendar, text: t("forHostsSection.b2") },
    { icon: Wallet, text: t("forHostsSection.b3") },
  ];
  return (
    <section id="merchants" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
      <div className="overflow-hidden rounded-[2rem] border border-border bg-gradient-to-br from-[oklch(0.18_0.02_260)] to-[oklch(0.22_0.04_260)] text-white shadow-elevated lg:rounded-[2.5rem]">
        <div className="grid gap-0 lg:grid-cols-2">
          <div className="p-8 sm:p-12 lg:p-16">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-white/80 backdrop-blur">
              <span className="flex h-1.5 w-1.5 rounded-full bg-[oklch(0.75_0.18_140)]" />
              {t("forHostsSection.eyebrow")}
            </div>
            <h2 className="mt-6 text-balance font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
              {t("forHostsSection.titleA")}{" "}
              <span className="bg-gradient-to-r from-[oklch(0.85_0.15_90)] to-[oklch(0.8_0.18_60)] bg-clip-text text-transparent">
                {t("forHostsSection.titleB")}
              </span>
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-white/70">
              {t("forHostsSection.subtitle")}
            </p>

            <ul className="mt-8 space-y-4">
              {bullets.map((b) => (
                <li key={b.text} className="flex items-start gap-3 text-sm text-white/85">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/10 text-white">
                    <b.icon className="h-4 w-4" />
                  </div>
                  <span className="pt-1">{b.text}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/partner"
                className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-foreground transition-transform hover:scale-[1.02]"
              >
                {t("forHostsSection.startListing")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
              </Link>

              <Dialog>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-white/10"
                  >
                    <Calculator className="h-4 w-4" />
                    {t("forHostsSection.calculate")}
                  </button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>{t("forHostsSection.calcTitle")}</DialogTitle>
                    <DialogDescription>{t("forHostsSection.calcDesc")}</DialogDescription>
                  </DialogHeader>
                  <EarningsCalculator />
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="relative min-h-[320px] lg:min-h-full">
            <img
              src={merchantImg}
              alt="A host welcoming guests into a beautiful space"
              width={1200}
              height={1400}
              loading="lazy"
                decoding="async"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[oklch(0.18_0.02_260)] via-transparent to-transparent lg:from-[oklch(0.18_0.02_260)]/40" />

            {/* Stat card overlay */}
            <div className="absolute bottom-6 start-6 end-6 rounded-2xl border border-white/10 bg-white/95 p-5 shadow-elevated backdrop-blur sm:bottom-10 sm:start-10 sm:end-auto sm:max-w-[260px]">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("forHostsSection.thisMonth")}</div>
              <div className="mt-1 font-display text-3xl font-bold text-foreground"><Riyal /> 3,840</div>
              <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1 rounded-full bg-[oklch(0.94_0.08_140)] px-2 py-0.5 text-[oklch(0.4_0.15_140)]">
                  <TrendingUp className="h-3 w-3" /> +24%
                </span>
                {t("forHostsSection.vsLast")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
